import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChooseAgencyStrategy,
  SiteAnnouncement,
  SiteAnnouncementFormData,
  SiteAnnouncementImageValue,
  SiteAnnouncementType
} from 'core/siteAnnouncement/SiteAnnouncement';
import { useCallAPI } from 'hooks/useCallAPI';
import moment from 'moment';
import { AgencyManager, DefaultAgencyManager } from 'core';
import i18n from 'i18n';
import {
  URL_REGEX,
  validateEmpty,
  validateMinimum,
  validateStringMaxLength,
  validateValidWords
} from 'utils/ValidateUtils';
import { isEmpty, omitBy } from 'lodash';
import {
  DefaultSiteAnnouncementManager,
  SiteAnnouncementManager
} from 'core/siteAnnouncement/SiteAnnouncementManager';
import { FormConfig, FormSection } from 'components/common/form/FormConfig';
import { getByteLength } from 'utils/StringUtil';
import { FormikContextType, FormikErrors } from 'formik';
import styles from './siteAnnouncementForm.module.scss';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { useParams } from 'react-router-dom';
import { renderAddUrlModal } from './AddUrlModal';
import {
  renderReadOnlyAgencies,
  renderReadOnlyAttachments,
  renderReadOnlyImage
} from './SiteAnnouncementReadOnlyFields';

export type SiteAnnouncementSetupFlowPageModel = {
  loading: boolean;
  title: string;
  redirectPath?: string;
  siteAnnouncement?: SiteAnnouncementFormData;
  allAgencyOptions: SelectOptions[];
  isPublished: boolean;
  validateForm: (
    values: SiteAnnouncementFormData
  ) => Record<string, string | undefined>;
  getFormConfig: (
    props: FormikContextType<SiteAnnouncementFormData>
  ) => FormConfig;
  onSubmit: (values: SiteAnnouncementFormData) => void;
  onCancel: () => void;
  setSiteAnnouncement: (siteAnnouncement: SiteAnnouncementFormData) => void;
};

type BaseSiteAnnouncementSetupFlowPageModel = {
  loading: boolean;
  redirectPath?: string;
  allAgencyOptions: SelectOptions[];
  callAPIs: (
    apiCalls: (() => Promise<any>)[],
    onSuccess?: (...args: any[]) => void
  ) => void;
  onCancel: () => void;
  setRedirectPath: (path: string) => void;
  getBasicSection: (
    name: string,
    isPublished?: boolean,
    shouldResend?: boolean
  ) => FormSection;
  getContentSection: (isPublished?: boolean) => FormSection;
  getAdvancedSection: (
    duration: number,
    isPublished: boolean,
    onShouldResendChange?: (value: boolean) => void
  ) => FormSection;
  getAgencyIdsSection: (
    chooseAgencyStrategy: ChooseAgencyStrategy,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<SiteAnnouncementFormData>>,
    isPublished?: boolean
  ) => FormSection;
  validateForm: (
    values: SiteAnnouncementFormData
  ) => Record<string, string | undefined>;
};

const chooseAgencyStrategyOptions = createSelectOptionsFromEnum(
  ChooseAgencyStrategy,
  'siteAnnouncementForm.labels.',
  [],
  (value) => `${value.toLowerCase()}Agencies`
);

const siteAnnouncementTypeOptions = createSelectOptionsFromEnum(
  SiteAnnouncementType,
  'siteAnnouncement.types.'
);

// Allowed attachment file extensions (used both for the native picker `accept`
// filter and for validation).
const ATTACHMENT_ALLOWED_EXTENSIONS = ['doc', 'docx', 'pdf', 'pptx', 'png'];
const ATTACHMENT_ACCEPT_TYPES = ATTACHMENT_ALLOWED_EXTENSIONS.map(
  (extension) => `.${extension}`
);
// Combined size of all uploaded attachments must not exceed 10MB.
const MAX_ATTACHMENTS_TOTAL_SIZE = 10 * 1024 * 1024;

const defaultAgencyManager: AgencyManager = new DefaultAgencyManager();
const defaultSiteAnnouncementManager: SiteAnnouncementManager =
  new DefaultSiteAnnouncementManager();

const useBaseSiteAnnouncementSetupFlowPageModel = (
  isPublished = false
): BaseSiteAnnouncementSetupFlowPageModel => {
  const { loading, callAPIs } = useCallAPI();
  const [redirectPath, setRedirectPath] = useState<string>();
  const [allAgencyOptions, setAllAgencyOptions] = useState<SelectOptions[]>([]);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    callAPIs(
      [() => defaultAgencyManager.getAgenciesOptions()],
      (agencyOptions: SelectOptions[]) => {
        setAllAgencyOptions(
          agencyOptions.map((option) => ({
            ...option,
            value: String(option.value)
          }))
        );
      }
    );
  }, [callAPIs]);

  const getBasicSection = useCallback(
    (name: string, isPublished = false, shouldResend = false) => {
      const fieldsBuilder = new FormConfig.FieldsBuilder();
      // once published and not resending, the schedule is fixed: show it
      // read-only so the user can't change the publish time
      if (isPublished && !shouldResend) {
        fieldsBuilder.addFormikLabel({
          label: i18n.t('siteAnnouncementForm.labels.publishTime'),
          name: 'publishTime'
        });
      } else {
        fieldsBuilder.addFormikDatePicker({
          label: i18n.t('siteAnnouncementForm.labels.publishTime'),
          name: 'publishTime',
          showTimePicker: true,
          minDate: moment()
            .add(1, 'hour')
            .startOf('hour')
            .format('YYYY-MM-DD HH:mm:ss')
        });
      }
      // type is locked once an announcement is published: show its label instead
      if (isPublished) {
        fieldsBuilder.addFormikLabel({
          label: i18n.t('siteAnnouncementForm.labels.type'),
          name: 'type',
          formatter: (value: string) =>
            i18n.t(
              `siteAnnouncement.types.${SiteAnnouncementType[value].toLowerCase()}`
            )
        });
      } else {
        fieldsBuilder.addFormikSelect({
          label: i18n.t('siteAnnouncementForm.labels.type'),
          name: 'type',
          simpleValue: true,
          options: siteAnnouncementTypeOptions
        });
      }
      fieldsBuilder.addFormikInput({
        label: i18n.t('siteAnnouncementForm.labels.name'),
        name: 'name',
        className: styles.nameInput,
        postText: `${getByteLength(name)}/50`
      });
      return new FormConfig.SectionBuilder(fieldsBuilder.build())
        .withTitle(i18n.t('siteAnnouncementForm.titles.basicSettingSection'))
        .build();
    },
    []
  );

  const getAdvancedSection = useCallback(
    (
      duration: number,
      isPublished: boolean,
      onShouldResendChange?: (value: boolean) => void
    ) => {
      const fieldsBuilder = new FormConfig.FieldsBuilder().addFormikInput({
        label: i18n.t('siteAnnouncementForm.labels.duration'),
        name: 'duration',
        type: 'number',
        fieldContentWidth: 64,
        min: 1,
        preText: i18n.t('siteAnnouncementForm.labels.prefix.duration'),
        postText: i18n.t('siteAnnouncementForm.labels.postfix.duration', {
          plural: duration > 1 ? 's' : ''
        })
      });
      if (isPublished) {
        fieldsBuilder.addFormikCheckbox({
          label: i18n.t('siteAnnouncementForm.labels.shouldResend'),
          name: 'shouldResend',
          onChange: onShouldResendChange
        });
      }
      return new FormConfig.SectionBuilder(fieldsBuilder.build())
        .withTitle(i18n.t('siteAnnouncementForm.titles.advancedSection'))
        .build();
    },
    []
  );

  const getContentSection = useCallback((isPublished = false) => {
    const fieldsBuilder = new FormConfig.FieldsBuilder();
    // image is locked once an announcement is published: show the image itself
    if (isPublished) {
      fieldsBuilder.addFormikCustom({
        label: i18n.t('siteAnnouncementForm.labels.imageUrl'),
        name: 'image',
        render: ({ values }) => renderReadOnlyImage(values.image)
      });
    } else {
      fieldsBuilder.addFormikFileInput({
        type: 'image',
        label: i18n.t('siteAnnouncementForm.labels.imageUrl'),
        name: 'image',
        validTypes: ['image/jpeg', 'image/jpg', 'image/png'],
        hints: [
          i18n.t('siteAnnouncementForm.hints.fileType', {
            type: 'png / jpg'
          }),
          i18n.t('creativeSetupFlow.labels.storageHint', {
            storage: '2MB'
          })
        ]
      });
    }
    fieldsBuilder.addFormikTextarea({
      label: i18n.t('siteAnnouncementForm.labels.content'),
      name: 'content',
      innerRef: contentRef
    });
    fieldsBuilder.addCustom({
      label: '',
      name: 'addUrl',
      render: () => renderAddUrlModal(contentRef)
    });
    // attachments are locked once an announcement is published: show file names
    if (isPublished) {
      fieldsBuilder.addFormikCustom({
        label: i18n.t('siteAnnouncementForm.labels.attachmentUrls'),
        name: 'attachments',
        render: ({ values }) => renderReadOnlyAttachments(values.attachments)
      });
    } else {
      fieldsBuilder.addFormikMultiFileInput({
        label: i18n.t('siteAnnouncementForm.labels.attachmentUrls'),
        name: 'attachments',
        validTypes: ATTACHMENT_ACCEPT_TYPES,
        hints: [
          i18n.t('siteAnnouncementForm.hints.fileType', {
            type: 'doc / docx / pdf / pptx / png'
          }),
          i18n.t('siteAnnouncementForm.hints.allFileStorage', {
            storage: '10MB'
          })
        ]
      });
    }
    return new FormConfig.SectionBuilder(fieldsBuilder.build())
      .withTitle(i18n.t('siteAnnouncementForm.titles.contentSection'))
      .build();
  }, []);

  const getAgencyIdsSection = useCallback(
    (
      chooseAgencyStrategy: ChooseAgencyStrategy,
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean
      ) => Promise<void | FormikErrors<SiteAnnouncementFormData>>,
      isPublished = false
    ) => {
      const fieldsBuilder = new FormConfig.FieldsBuilder();
      // agency targeting is locked once an announcement is published: show
      // the chosen agencies' labels instead of the editable radio + select
      if (isPublished) {
        fieldsBuilder.addFormikCustom({
          label: i18n.t('siteAnnouncementForm.labels.chooseVendorStrategy'),
          name: 'agencyIds',
          render: ({ values }) =>
            renderReadOnlyAgencies(
              values.chooseAgencyStrategy,
              values.agencyIds,
              allAgencyOptions
            )
        });
        return new FormConfig.SectionBuilder(fieldsBuilder.build())
          .withTitle(i18n.t('siteAnnouncementForm.titles.agencySection'))
          .build();
      }
      fieldsBuilder.addFormikRadio({
        label: i18n.t('siteAnnouncementForm.labels.chooseVendorStrategy'),
        name: 'chooseAgencyStrategy',
        options: chooseAgencyStrategyOptions,
        onChange: () => {
          setFieldValue('agencyIds', []);
        }
      });
      if (chooseAgencyStrategy === ChooseAgencyStrategy.PARTIAL) {
        fieldsBuilder.addFormikSelect({
          label: '',
          name: 'agencyIds',
          placeholder: i18n.t('siteAnnouncementForm.placeholders.agencies'),
          options: allAgencyOptions,
          isMulti: true,
          simpleValue: true
        });
      }
      return new FormConfig.SectionBuilder(fieldsBuilder.build())
        .withTitle(i18n.t('siteAnnouncementForm.titles.agencySection'))
        .build();
    },
    [allAgencyOptions]
  );

  const validatePublishTime = useCallback(
    (publishTime: string, shouldResend: boolean) => {
      // a published announcement that isn't being resent keeps its original
      // schedule (shown read-only), so there's nothing to validate
      if (isPublished && !shouldResend) {
        return undefined;
      }
      const error = validateEmpty(publishTime);
      if (error) {
        return error;
      }
      const publishMoment = moment(publishTime, 'YYYY-MM-DD HH:mm:ss');
      if (publishMoment.isBefore(moment().add(1, 'hour').startOf('hour'))) {
        return i18n.t('siteAnnouncementForm.errors.publishTimeTooEarly');
      }
      return undefined;
    },
    [isPublished]
  );

  const validateAgencyIds = useCallback(
    (chooseAgencyStrategy: ChooseAgencyStrategy, agencyIds: string[]) => {
      if (chooseAgencyStrategy === ChooseAgencyStrategy.ALL) {
        return;
      }

      return validateEmpty(agencyIds);
    },
    []
  );

  const validInputFieldValidityAndLength = useCallback(
    (
      value: string,
      maxLength: number,
      lengthErrorMessage: string,
      validityErrorMessage: string
    ) => {
      const lengthError = validateStringMaxLength(
        value,
        maxLength,
        lengthErrorMessage
      );
      if (lengthError) {
        return lengthError;
      }
      const validityError = validateValidWords(value, validityErrorMessage);
      if (validityError) {
        return validityError;
      }
      return undefined;
    },
    []
  );

  const validateContent = useCallback(
    (content: string) => {
      const replacedContent = content.replace(
        new RegExp(`{${URL_REGEX.source}}`, 'g'),
        '$1'
      );
      return validInputFieldValidityAndLength(
        replacedContent,
        300,
        i18n.t('siteAnnouncementForm.errors.contentLength'),
        i18n.t('siteAnnouncementForm.errors.contentInvalid')
      );
    },
    [validInputFieldValidityAndLength]
  );

  const validateImage = useCallback((image: SiteAnnouncementImageValue) => {
    // image is optional; only validate a freshly picked file
    const file = image?.file;
    if (!file) {
      return undefined;
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return i18n.t('siteAnnouncementForm.hints.fileType', {
        type: 'png / jpg'
      });
    }
    const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB
    if (file.size > MAX_IMAGE_SIZE) {
      return i18n.t('creativeSetupFlow.labels.storageHint', {
        storage: '2MB'
      });
    }
    return undefined;
  }, []);

  const validateAttachments = useCallback(
    (attachments: SiteAnnouncementFormData['attachments']) => {
      if (!attachments || attachments.length === 0) {
        return undefined;
      }
      // only newly picked files carry a `file`; existing url-only entries are
      // already-persisted attachments that don't need re-validating
      const hasInvalidType = attachments.some((attachment) => {
        if (!attachment.file) {
          return false;
        }
        const extension = attachment.name.split('.').pop()?.toLowerCase();
        return !extension || !ATTACHMENT_ALLOWED_EXTENSIONS.includes(extension);
      });
      if (hasInvalidType) {
        return i18n.t('siteAnnouncementForm.hints.fileType', {
          type: 'doc / docx / pdf / pptx / png'
        });
      }
      const totalSize = attachments.reduce(
        (sum, attachment) => sum + (attachment.file?.size ?? 0),
        0
      );
      if (totalSize > MAX_ATTACHMENTS_TOTAL_SIZE) {
        return i18n.t('siteAnnouncementForm.hints.allFileStorage', {
          storage: '10MB'
        });
      }
      return undefined;
    },
    []
  );

  const validateForm = useCallback(
    (values: SiteAnnouncementFormData) => {
      return omitBy(
        {
          name: validInputFieldValidityAndLength(
            values.name,
            50,
            i18n.t('siteAnnouncementForm.errors.nameLength'),
            i18n.t('siteAnnouncementForm.errors.nameInvalid')
          ),
          content: validateContent(values.content),
          publishTime: validatePublishTime(
            values.publishTime,
            values.shouldResend
          ),
          duration: validateMinimum(values.duration, 1),
          image: validateImage(values.image),
          attachments: validateAttachments(values.attachments),
          agencyIds: validateAgencyIds(
            values.chooseAgencyStrategy,
            values.agencyIds
          )
        },
        isEmpty
      );
    },
    [
      validatePublishTime,
      validateAgencyIds,
      validateImage,
      validateAttachments,
      validInputFieldValidityAndLength,
      validateContent
    ]
  );

  const onCancel = useCallback(() => {
    setRedirectPath('/site-announcements');
  }, []);

  return {
    loading,
    redirectPath,
    allAgencyOptions,
    callAPIs,
    onCancel,
    validateForm,
    setRedirectPath,
    getBasicSection,
    getContentSection,
    getAdvancedSection,
    getAgencyIdsSection
  };
};

export const useCreateSiteAnnouncementSetupFlowPageModel =
  (): SiteAnnouncementSetupFlowPageModel => {
    const {
      loading,
      redirectPath,
      allAgencyOptions,
      callAPIs,
      onCancel,
      validateForm,
      setRedirectPath,
      getBasicSection,
      getContentSection,
      getAdvancedSection,
      getAgencyIdsSection
    } = useBaseSiteAnnouncementSetupFlowPageModel();

    const [siteAnnouncement, setSiteAnnouncement] =
      useState<SiteAnnouncementFormData>({
        // 最早只能設定當前下一個小時發佈
        publishTime: moment()
          .add(1, 'hour')
          .startOf('hour')
          .format('YYYY-MM-DD HH:mm:ss'),
        name: '',
        content: '',
        duration: 30,
        agencyIds: [],
        type: SiteAnnouncementType.GENERAL,
        image: {},
        attachments: [],
        shouldResend: false,
        chooseAgencyStrategy: ChooseAgencyStrategy.ALL
      });

    const onSubmit = useCallback(
      (_values: SiteAnnouncementFormData) => {
        callAPIs(
          [
            () => defaultSiteAnnouncementManager.createSiteAnnouncement(_values)
          ],
          () => {
            setRedirectPath('/site-announcements');
          }
        );
      },
      [callAPIs, setRedirectPath]
    );

    const getFormConfig = useCallback(
      ({
        values,
        setFieldValue
      }: FormikContextType<SiteAnnouncementFormData>) => {
        const { name, duration, chooseAgencyStrategy } = values;

        return new FormConfig.Builder()
          .addSection(getBasicSection(name))
          .addSection(getContentSection())
          .addSection(getAdvancedSection(duration, false))
          .addSection(getAgencyIdsSection(chooseAgencyStrategy, setFieldValue))
          .build();
      },
      [
        getBasicSection,
        getContentSection,
        getAdvancedSection,
        getAgencyIdsSection
      ]
    );

    return {
      title: i18n.t('siteAnnouncementForm.titles.create'),
      loading,
      redirectPath,
      siteAnnouncement,
      allAgencyOptions,
      isPublished: false,
      onCancel,
      onSubmit,
      validateForm,
      getFormConfig,
      setSiteAnnouncement
    };
  };

export const useEditSiteAnnouncementSetupFlowPageModel =
  (): SiteAnnouncementSetupFlowPageModel => {
    const [isPublished, setIsPublished] = useState<boolean>(false);
    const {
      loading,
      redirectPath,
      allAgencyOptions,
      callAPIs,
      onCancel,
      validateForm,
      setRedirectPath,
      getBasicSection,
      getContentSection,
      getAdvancedSection,
      getAgencyIdsSection
    } = useBaseSiteAnnouncementSetupFlowPageModel(isPublished);

    const [siteAnnouncement, setSiteAnnouncement] =
      useState<SiteAnnouncementFormData>();
    // the announcement's original schedule, captured once at load so it can be
    // restored if the user turns resending back off after editing the time
    const originalPublishTimeRef = useRef<string>('');
    const { id } = useParams<{ id: string }>();

    useEffect(() => {
      callAPIs(
        [() => defaultSiteAnnouncementManager.getSiteAnnouncement(+id)],
        (siteAnnouncement: SiteAnnouncement) => {
          setSiteAnnouncement({
            ...siteAnnouncement,
            // server returns agencyId as number; normalize to string so the
            // values match allAgencyOptions (whose value is String(id))
            agencyIds: (siteAnnouncement.agencyIds || []).map((agencyId) =>
              String(agencyId)
            ),
            type: siteAnnouncement.type ?? SiteAnnouncementType.GENERAL,
            image: { url: siteAnnouncement.imageUrl },
            attachments: (siteAnnouncement.attachmentUrls || []).map((url) => ({
              url,
              name: url.split('/').pop() || url
            })),
            chooseAgencyStrategy: isEmpty(siteAnnouncement.agencyIds)
              ? ChooseAgencyStrategy.ALL
              : ChooseAgencyStrategy.PARTIAL
          });
          // An announcement counts as "published" once its publish time has
          // been reached, regardless of status: a deactivated (INACTIVE)
          // announcement was already published too, so relying on
          // `status === PUBLISHED` alone would wrongly unlock its fields.
          setIsPublished(
            moment(siteAnnouncement.publishTime).isSameOrBefore(moment())
          );
          originalPublishTimeRef.current = siteAnnouncement.publishTime;
        }
      );
    }, [id, callAPIs]);

    const onSubmit = useCallback(
      (_values: SiteAnnouncementFormData) => {
        callAPIs(
          [
            () =>
              defaultSiteAnnouncementManager.updateSiteAnnouncement(
                +id,
                _values
              )
          ],
          () => {
            setRedirectPath('/site-announcements');
          }
        );
      },
      [id, callAPIs, setRedirectPath]
    );

    const getFormConfig = useCallback(
      ({
        values,
        setFieldValue
      }: FormikContextType<SiteAnnouncementFormData>) => {
        const { name, duration, chooseAgencyStrategy, shouldResend } = values;

        return new FormConfig.Builder()
          .addSection(getBasicSection(name, isPublished, shouldResend))
          .addSection(getContentSection(isPublished))
          .addSection(
            getAdvancedSection(duration, isPublished, (value) => {
              // restore the original schedule when resending is turned off
              if (!value) {
                setFieldValue('publishTime', originalPublishTimeRef.current);
              }
            })
          )
          .addSection(
            getAgencyIdsSection(
              chooseAgencyStrategy,
              setFieldValue,
              isPublished
            )
          )
          .build();
      },
      [
        isPublished,
        getBasicSection,
        getContentSection,
        getAdvancedSection,
        getAgencyIdsSection
      ]
    );

    return {
      title: i18n.t('siteAnnouncementForm.titles.edit'),
      loading,
      redirectPath,
      siteAnnouncement,
      allAgencyOptions,
      isPublished,
      onSubmit,
      onCancel,
      validateForm,
      getFormConfig,
      setSiteAnnouncement
    };
  };
